import { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Input from "../../atoms/Input";
import { useAppSelector } from "redux/hooks";
import Button from "components/atoms/Button/Button";
import ToasterService from "utils/toaster.util";
import BookingService from "services/booking.service";
import CircularProgress from "@mui/material/CircularProgress";
import { Box, Chip } from "@mui/material";

type BookingOption = {
  value: string;
  label: string;
  name?:string,
};

export default function SelectAutoCompleteBookings({
  input,
  meta,
  disabled,
  disabledOnUpdate,
  label,
  isMobile,
  ...rest
}: any) {

  const bookings = useAppSelector((state) => state.booking["search"].bookings);
  const bookingOptions = useAppSelector((state) => state.booking.bookingOptions);

  const [typedValue, setTypedValue] = useState("");
  const [values, setValues] = useState<BookingOption[]>([]);
  const [Open, setOpen] = useState(false);
  const [bookingList, setBookingList] = useState<BookingOption[]>([]);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setBookingList(bookingOptions);
  }, [bookingOptions]);


  useEffect(() => {
    if (input?.value && Array.isArray(input.value)) {
      setValues(input.value);
    } else {
      setValues([]); 
    }
  }, [input?.value]);

  const handleOnInputChange = (event: any, newInputValue: string) => {
    setTypedValue(newInputValue);
    if (debounceTimer) clearTimeout(debounceTimer);
    if (!newInputValue.trim()) {
      setBookingList([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    const newTimer = setTimeout(() => {
      triggerSearch(newInputValue);
    }, 400);
    setDebounceTimer(newTimer);
  };

  const triggerSearch = async (search: string) => {
    if (!search?.trim()) {
      setLoading(false);
      return;
    }
    try {
      const body = { searchData: search };
      const [success] = await BookingService.getSearchBookings(body);
      if (success) setOpen(true);
    } catch (error) {
      ToasterService.showError("Failed to fetch bookings");
    } finally {
      setLoading(false);
    }
  };

  const handleOnChange = (event: any, selectedOptions: BookingOption[]) => {
    setValues(selectedOptions);
    input?.onChange(selectedOptions);
    setBookingList([]);
    setOpen(false);
    setTypedValue("");
  };

  const handleSearchOnMobile = async () => {
    if (!typedValue.trim()) {
      ToasterService.showError("Type something to search bookings");
      return;
    }
    setLoading(true);
    try {
      await triggerSearch(typedValue);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Autocomplete<BookingOption, true, false, false>
        {...rest}
        multiple
        value={values}
        options={bookingList || []}
        getOptionLabel={(option) => {
          const bookingObj = bookings.find((b) => b._id === option.value);
          if (!bookingObj) return option.label || "";
          const bookingNumber = bookingObj?.booking_number;
          const bookingForName = [
            bookingObj?.booking_for_details?.first_name,
            bookingObj?.booking_for_details?.last_name,
          ]
            .filter(Boolean)
            .join(" ");
          const phone = bookingObj?.booking_for_details?.phone;
          const labelParts = [bookingNumber, bookingForName, phone].filter(
            Boolean
          );
          if (labelParts.length === 0) return "";
          return labelParts.join(" - ");
        }}
        open={Open}
        disablePortal
        disabled={disabledOnUpdate ? true : disabled}
        onChange={handleOnChange}
        onInputChange={handleOnInputChange}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        loading={loading}
        onClose={() => setOpen(false)}  
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Box key={index} sx={{ mt: 1, mb: 0.3 }}>
              <Chip
                {...getTagProps({ index })}
                label={
                  option.name !== " "
                    ? `${option.label} - ${option.name}`
                    : option.label
                }
              />
            </Box>
          ))
        }
        renderInput={(params) => (
          <Input
            {...params}
            label={label}
            placeholder="Type something to search bookings"
            error={meta.touched && meta.error}
            helperText={meta.touched && meta.error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {isMobile && (
        <Button
          onClick={handleSearchOnMobile}
          disabled={loading}
          style={{ marginTop: 8 }}
        >
          {loading ? <CircularProgress size={24} /> : "Search"}
        </Button>
      )}
    </>
  );
}
